import { isAxiosError } from 'axios';
import { useContext, useEffect, useState } from 'react';
import { Svgs } from '../../assets/svg';
import { Page, Spacer } from '../../components';
import {
  MutationStatus,
  MutatorState,
  useAppStorage,
  useMutation,
  useObservable,
} from '@aesop-fables/scrinium';
import { useService } from '@aesop-fables/containr-react';
import { UserCompartments, userStorageKey } from '../../data/user';
import { UserData } from '../../models/UserData';
import { useLocation, useNavigate } from 'react-router';
import { OrganizationCompartments, organizationStorageKey } from '../../data/organization';
import { OrganizationData } from '../../models/OrganizationData';
import { DeleteOrganization } from '../../data/organization/mutations';
import { ToastbarContext } from '../../App';
import { Toast } from '../../models/Toast';
import { OrganizationMetricsData } from '../../api/apis/OrgPricebookLicenseMetricsApi';
import { SignUpWizard, signUpWizardKey } from '../../services/signUp';
import { useLoading } from '../../hooks/useLoading';
import { SignUpCompartments, signUpStorageKey } from '../../data/signUp';
import { OrgSignUpStateData } from '../../models/SignUpData';
import IncompleteOrgs from './IncompleteOrgs';
import AdminTierHeader from '../multitier/AdminTierHeader';
import { AdminTierTypeEnum, DrillDownWizard, drillDownWizardKey } from '../../services/drillDown';
import { TeamCompartments, teamStorageKey } from '../../data/team';
import { TeamData } from '../../models/TeamData';
import { OrgStatus } from '../multitier/AdminDashboard';
import { OrgListView } from './OrgListView';

interface OrgPageProps {
  select: MutatorState<number>;
  setOrgStatus: React.Dispatch<React.SetStateAction<OrgStatus | undefined>>;
}

const OrgPage: React.FC<OrgPageProps> = ({ select, setOrgStatus }) => {
  const appStorage = useAppStorage();
  const navigate = useNavigate();
  const location = useLocation();
  const { setLoading } = useLoading();
  const { setToast } = useContext(ToastbarContext);
  const [tryAgain, setTryAgain] = useState<boolean>(false);
  const [removeModalOpen, setRemoveModalOpen] = useState<boolean>(false);

  const signUpWizard = useService<SignUpWizard>(signUpWizardKey);
  const drillDownWizard = useService<DrillDownWizard>(drillDownWizardKey);
  const deleteOrganization = useMutation(new DeleteOrganization());

  const userDataCache = appStorage.retrieve<UserCompartments>(userStorageKey);
  const organizationDataCache =
    appStorage.retrieve<OrganizationCompartments>(organizationStorageKey);
  const signUpDataCache = appStorage.retrieve<SignUpCompartments>(signUpStorageKey);
  const teamDataCache = appStorage.retrieve<TeamCompartments>(teamStorageKey);

  const highestAccess = useObservable(drillDownWizard.highestAccess$);
  const origin = useObservable(drillDownWizard.origin$);
  const userData = useObservable(userDataCache.observe$<UserData>('user'));
  const allOrgSignUpStates = useObservable(
    signUpDataCache.observe$<OrgSignUpStateData[]>('allOrgSignUpStates'),
  );
  const allOrganizations = useObservable(
    organizationDataCache.observe$<OrganizationData[]>('allOrganizations'),
  );
  const allMetrics = useObservable(
    organizationDataCache.observe$<OrganizationMetricsData[]>('allMetrics'),
  );

  const teamName = useObservable(teamDataCache.observe$<TeamData>('team'))?.name ?? '';
  const numTeams = useObservable(teamDataCache.observe$<TeamData[]>('allTeams'))?.length ?? 0;

  const numOrgs = allOrganizations?.length ?? 0;
  const landingPage =
    (highestAccess === AdminTierTypeEnum.Organization && numOrgs >= 2) ||
    (highestAccess === AdminTierTypeEnum.Team && numTeams < 2) ||
    origin === AdminTierTypeEnum.Organization;

  const canCreateOrg =
    highestAccess === AdminTierTypeEnum.Team || highestAccess === AdminTierTypeEnum.Group;

  const incompleteOrgMetrics =
    allMetrics && allMetrics.length > 0
      ? allMetrics
          .filter(orgMetrics =>
            allOrgSignUpStates?.some(
              signUpState =>
                orgMetrics.organizationId === signUpState.id &&
                !signUpState.onboardingComplete &&
                !signUpState.offboardingComplete,
            ),
          )
          .sort((a, b) => a.organizationName.localeCompare(b.organizationName))
      : undefined;

  const showIncompleteOrgs = incompleteOrgMetrics && incompleteOrgMetrics.length > 0;

  useEffect(() => {
    setLoading(
      location.pathname.includes('organization') &&
        (!allOrganizations || !allMetrics || !allOrgSignUpStates),
    );
  }, [allOrganizations, allOrgSignUpStates, allMetrics, location.pathname, setLoading]);

  useEffect(() => {
    if (!tryAgain) {
      return;
    }
    if (deleteOrganization.error) {
      setLoading(false);
      const err = deleteOrganization.error;
      const status = isAxiosError(err) ? err.response?.status : undefined;
      setToast(
        new Toast({
          message:
            status === 400
              ? 'Unable to remove organization with an active license agreement'
              : 'Error removing organization',
          severity: 'error',
          open: true,
        }),
      );
      setTryAgain(false);
    } else if (deleteOrganization.status === MutationStatus.Complete) {
      setLoading(false);
      setRemoveModalOpen(false);
      setToast(new Toast({ message: 'Organization removed!', severity: 'success', open: true }));
      setTryAgain(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteOrganization, tryAgain]);

  if (
    allMetrics === undefined ||
    allOrganizations === undefined ||
    allOrgSignUpStates === undefined
  ) {
    return null;
  }

  const completeOrgMetrics = allMetrics
    .filter(orgMetrics =>
      allOrgSignUpStates.some(
        signUpState =>
          orgMetrics.organizationId === signUpState.id &&
          (signUpState.onboardingComplete || signUpState.offboardingComplete),
      ),
    )
    .sort((a, b) => a.organizationName.localeCompare(b.organizationName));

  const onCreate = () => {
    signUpWizard.reset();
    navigate('/onboarding/aboutYourOrg');
  };

  return (
    <Page
      title={landingPage ? (userData?.firstName ? `Hi, ${userData.firstName}!` : 'Hi!') : teamName}>
      <Spacer height='sm' />
      {showIncompleteOrgs && (
        <>
          <IncompleteOrgs
            incompleteOrgMetrics={incompleteOrgMetrics}
            setRemoveModalOpen={setRemoveModalOpen}
            setTryAgain={setTryAgain}
            setOrgStatus={setOrgStatus}
            deleteOrganization={deleteOrganization}
            select={select}
            removeModalOpen={removeModalOpen}
          />
          <Spacer height='sm' />
        </>
      )}
      <AdminTierHeader
        onCreate={onCreate}
        hideCreate={!canCreateOrg || showIncompleteOrgs}
        tierIcon={<Svgs.ArtDashboardOrganization />}
        tier='organization'
      />
      <OrgListView
        allMetrics={completeOrgMetrics}
        orgList={allOrganizations}
        select={select}
        setOrgStatus={setOrgStatus}
      />
    </Page>
  );
};

export default OrgPage;
